import { useEffect, useState, useCallback } from "react";
import { api, formatRupiah, KATEGORI, FRANCHISES, FRANCHISE_CLS } from "@/lib/api";
import { PageHeader, Badge, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Search, Boxes, Pencil, Trash2, X, Upload, Download } from "lucide-react";
import { CurrencyInput } from "@/components/CurrencyInput";

const emptyForm = {
  nama_produk: "",
  kategori: "sealed",
  tcg_franchise: "Pokemon",
  stok_qty: 0,
  harga_beli: 0,
  harga_jual: 0,
  lokasi_penyimpanan: "",
  set_name: "",
  type_sealed: "",
  rarity: "",
  condition: "",
  foil_type: "",
  grading_company: "",
  grade_score: "",
  cert_number: "",
  catatan: "",
};

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-indigo-500 transition";

function ItemModal({ open, onClose, onSaved, editing, allowed = ["sealed"] }) {
  const [form, setForm] = useState({ ...emptyForm, kategori: allowed[0] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) setForm({ ...emptyForm, ...editing });
    else setForm({ ...emptyForm, kategori: allowed[0] });
  }, [editing, open]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.nama_produk.trim()) return toast.error("Nama produk wajib diisi");
    setSaving(true);
    const payload = {
      ...form,
      stok_qty: Number(form.stok_qty),
      harga_beli: Number(form.harga_beli),
      harga_jual: Number(form.harga_jual),
    };
    try {
      if (editing) await api.put(`/inventory/${editing.id}`, payload);
      else await api.post("/inventory", payload);
      toast.success(editing ? "Produk diperbarui" : "Produk ditambahkan");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-800 bg-[#131822] p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold text-white">{editing ? "Edit Produk" : "Tambah Produk"}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="close-inventory-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-400 mb-1.5">Nama Produk</label>
            <input data-testid="inv-nama-input" className={inputCls} value={form.nama_produk} onChange={(e) => set("nama_produk", e.target.value)} placeholder="Contoh: Scarlet & Violet Booster Box" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Kategori</label>
            <select data-testid="inv-kategori-select" className={inputCls} value={form.kategori} onChange={(e) => set("kategori", e.target.value)}>
              {allowed.includes("sealed") && <option value="sealed">Sealed Product</option>}
              {allowed.includes("raw") && <option value="raw">Raw Cards</option>}
              {allowed.includes("slab") && <option value="slab">Slab / Graded</option>}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">TCG / Franchise</label>
            <select data-testid="inv-franchise-select" className={inputCls} value={form.tcg_franchise} onChange={(e) => set("tcg_franchise", e.target.value)}>
              {FRANCHISES.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Set / Seri</label>
            <input className={inputCls} value={form.set_name} onChange={(e) => set("set_name", e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Lokasi Penyimpanan</label>
            <input className={inputCls} value={form.lokasi_penyimpanan} onChange={(e) => set("lokasi_penyimpanan", e.target.value)} placeholder="Rak A1" />
          </div>

          {form.kategori === "sealed" && (
            <div className="sm:col-span-2">
              <label className="block text-xs text-slate-400 mb-1.5">Tipe Sealed</label>
              <input className={inputCls} value={form.type_sealed} onChange={(e) => set("type_sealed", e.target.value)} placeholder="Booster Box / ETB / Blister / Case" />
            </div>
          )}
          {form.kategori === "raw" && (
            <>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Rarity</label>
                <input className={inputCls} value={form.rarity} onChange={(e) => set("rarity", e.target.value)} placeholder="SR / SAR / AR" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Kondisi</label>
                <select className={inputCls} value={form.condition} onChange={(e) => set("condition", e.target.value)}>
                  <option value="">-</option>
                  <option>NM</option>
                  <option>LP</option>
                  <option>MP</option>
                  <option>HP</option>
                </select>
              </div>
            </>
          )}
          {form.kategori === "slab" && (
            <>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Grading Company</label>
                <select className={inputCls} value={form.grading_company} onChange={(e) => set("grading_company", e.target.value)}>
                  <option value="">-</option>
                  <option>PSA</option>
                  <option>BGS</option>
                  <option>CGC</option>
                  <option>ARS</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Grade</label>
                <input className={inputCls} value={form.grade_score} onChange={(e) => set("grade_score", e.target.value)} placeholder="10 / 9.5" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-400 mb-1.5">Nomor Sertifikat</label>
                <input className={inputCls} value={form.cert_number} onChange={(e) => set("cert_number", e.target.value)} />
              </div>
            </>
          )}

          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Stok (Qty)</label>
            <input data-testid="inv-stok-input" type="number" className={inputCls} value={form.stok_qty} onChange={(e) => set("stok_qty", e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Harga Beli (Modal)</label>
            <CurrencyInput value={form.harga_beli} onChange={(v) => set("harga_beli", v)} className={inputCls} data-testid="inv-beli-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Harga Jual</label>
            <CurrencyInput value={form.harga_jual} onChange={(v) => set("harga_jual", v)} className={inputCls} data-testid="inv-jual-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-400 mb-1.5">Catatan</label>
            <input className={inputCls} value={form.catatan} onChange={(e) => set("catatan", e.target.value)} />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition">
            Batal
          </button>
          <button data-testid="save-inventory-button" onClick={save} disabled={saving} className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-60">
            {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function parseCsv(text) {
  const rows = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length === 0) return { headers: [], rows: [] };
  const splitLine = (line) => {
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (ch === "," && !inQ) {
        out.push(cur); cur = "";
      } else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const headers = splitLine(lines[0]).map((h) => h.toLowerCase());
  for (let i = 1; i < lines.length; i++) {
    const cells = splitLine(lines[i]);
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = cells[idx] ?? ""; });
    rows.push(obj);
  }
  return { headers, rows };
}

const NUM_FIELDS = ["stok_qty", "harga_beli", "harga_jual"];
const CSV_TEMPLATE =
  "nama_produk,kategori,tcg_franchise,stok_qty,harga_beli,harga_jual,set_name,lokasi_penyimpanan,type_sealed,rarity,condition,grading_company,grade_score,cert_number\n" +
  "Scarlet & Violet Booster Box,sealed,Pokemon,10,1500000,1900000,Scarlet & Violet,Rak A1,Booster Box,,,,,\n" +
  "Luffy Gear 5 Leader,raw,One Piece,5,80000,150000,OP-05,Rak B2,,SR,NM,,,\n" +
  "Charizard Base Set,slab,Pokemon,1,5000000,8500000,Base Set,Brankas,,,,PSA,9,12345678";

function BulkModal({ open, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setRows([]); setFileName(""); setError(""); }
  }, [open]);

  if (!open) return null;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers, rows: parsed } = parseCsv(String(ev.target.result || ""));
      if (!headers.includes("nama_produk") || !headers.includes("kategori")) {
        setError("CSV harus memiliki kolom minimal: nama_produk, kategori, tcg_franchise");
        setRows([]);
        return;
      }
      const cleaned = parsed
        .filter((r) => (r.nama_produk || "").trim())
        .map((r) => {
          const item = { ...r };
          NUM_FIELDS.forEach((f) => { item[f] = Number(r[f] || 0) || 0; });
          if (!["sealed", "raw", "slab"].includes((item.kategori || "").toLowerCase())) item.kategori = "sealed";
          else item.kategori = item.kategori.toLowerCase();
          if (!item.tcg_franchise) item.tcg_franchise = "Lainnya";
          return item;
        });
      setRows(cleaned);
      if (cleaned.length === 0) setError("Tidak ada baris valid ditemukan di file.");
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "template_stok_tcg.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (rows.length === 0) return toast.error("Tidak ada data untuk diimpor");
    setSaving(true);
    try {
      const { data } = await api.post("/inventory/bulk", { items: rows });
      toast.success(`${data.inserted} produk berhasil diimpor`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengimpor");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-800 bg-[#131822] p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-heading text-xl font-bold text-white">Impor Produk Massal (CSV)</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-slate-400 mb-5">Unggah file CSV. Kolom: <span className="font-mono text-xs text-indigo-300">nama_produk, kategori (sealed/raw/slab), tcg_franchise, stok_qty, harga_beli, harga_jual</span> dan kolom opsional lainnya.</p>

        <div className="flex flex-wrap gap-3 mb-5">
          <button onClick={downloadTemplate} data-testid="download-template-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium transition border border-slate-700">
            <Download className="w-4 h-4" /> Unduh Template
          </button>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 text-sm font-medium transition border border-indigo-500/40 cursor-pointer">
            <Upload className="w-4 h-4" /> Pilih File CSV
            <input type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" data-testid="csv-file-input" />
          </label>
          {fileName && <span className="text-xs text-slate-500 self-center font-mono">{fileName}</span>}
        </div>

        {error && <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 mb-4">{error}</div>}

        {rows.length > 0 && (
          <div className="rounded-lg border border-slate-800 overflow-hidden mb-5">
            <div className="px-4 py-2 bg-slate-900/60 text-xs font-mono uppercase text-slate-400 border-b border-slate-800">
              Pratinjau • {rows.length} produk siap diimpor
            </div>
            <div className="max-h-56 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-mono uppercase text-slate-500 border-b border-slate-800">
                    <th className="text-left px-4 py-2">Nama</th>
                    <th className="text-left px-4 py-2">Kategori</th>
                    <th className="text-left px-4 py-2">Game</th>
                    <th className="text-right px-4 py-2">Stok</th>
                    <th className="text-right px-4 py-2">Jual</th>
                  </tr>
                </thead>
                <tbody data-testid="csv-preview">
                  {rows.slice(0, 50).map((r, i) => (
                    <tr key={i} className="border-b border-slate-800/50">
                      <td className="px-4 py-1.5 text-white">{r.nama_produk}</td>
                      <td className="px-4 py-1.5 text-slate-400">{r.kategori}</td>
                      <td className="px-4 py-1.5 text-slate-400">{r.tcg_franchise}</td>
                      <td className="px-4 py-1.5 text-right font-mono text-slate-300">{r.stok_qty}</td>
                      <td className="px-4 py-1.5 text-right font-mono text-emerald-300">{formatRupiah(r.harga_jual)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition">Batal</button>
          <button data-testid="submit-bulk-button" onClick={submit} disabled={saving || rows.length === 0} className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-50">
            {saving ? "Mengimpor..." : `Impor ${rows.length || ""} Produk`}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function Inventory({ scope = "produk" }) {
  const allowed = scope === "produk" ? ["sealed"] : ["raw", "slab"];
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [kategori, setKategori] = useState("all");
  const [franchise, setFranchise] = useState("all");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (franchise !== "all") params.franchise = franchise;
    if (q) params.q = q;
    const { data } = await api.get("/inventory", { params });
    setItems(data);
  }, [franchise, q]);

  useEffect(() => {
    load();
  }, [load]);

  const del = async (id) => {
    if (!window.confirm("Hapus item ini?")) return;
    await api.delete(`/inventory/${id}`);
    toast.success("Item dihapus");
    load();
  };

  const tabs = scope === "produk"
    ? []
    : [
        { k: "all", label: "Semua" },
        { k: "raw", label: "Raw Cards" },
        { k: "slab", label: "Slab" },
      ];

  const visible = items.filter(
    (i) => allowed.includes(i.kategori) && (kategori === "all" || i.kategori === kategori)
  );

  const title = scope === "produk" ? "Stok Produk" : "Stok Kartu & Slab";
  const subtitle = scope === "produk" ? "Sealed Product" : "Raw Cards & Graded";
  const addLabel = scope === "produk" ? "Tambah Produk" : "Tambah Kartu";

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={
          isAdmin && (
            <div className="flex gap-2">
              <button
                data-testid="import-inventory-button"
                onClick={() => setBulkOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold transition border border-slate-700"
              >
                <Upload className="w-4 h-4" /> Impor CSV
              </button>
              <button
                data-testid="add-inventory-button"
                onClick={() => { setEditing(null); setModal(true); }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition shadow-lg shadow-indigo-600/30"
              >
                <Plus className="w-4 h-4" /> {addLabel}
              </button>
            </div>
          )
        }
      />

      <div className="flex flex-col lg:flex-row gap-3 mb-5">
        <div className="flex gap-1.5 flex-wrap">
          {tabs.map((t) => (
            <button
              key={t.k}
              data-testid={`filter-kategori-${t.k}`}
              onClick={() => setKategori(t.k)}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition ${
                kategori === t.k ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40" : "bg-slate-800/50 text-slate-400 border border-transparent hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-3 lg:ml-auto">
          <select value={franchise} onChange={(e) => setFranchise(e.target.value)} data-testid="filter-franchise" className={`${inputCls} w-40`}>
            <option value="all">Semua Game</option>
            {FRANCHISES.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <div className="relative flex-1 lg:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input data-testid="inventory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari..." className={`${inputCls} pl-9`} />
          </div>
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyState icon={Boxes} title="Belum ada item" subtitle={scope === "produk" ? "Tambahkan produk sealed untuk mulai mencatat stok." : "Tambahkan raw cards atau slab untuk mulai mencatat stok."} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="inventory-grid">
          {visible.map((it) => {
            const k = KATEGORI[it.kategori] || KATEGORI.sealed;
            return (
              <div key={it.id} className="relative rounded-xl border border-slate-800 bg-[#131822] p-4 hover:border-slate-600 transition-all group">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex flex-wrap gap-1.5">
                    <Badge className={k.cls}>{k.label}</Badge>
                    <Badge className={FRANCHISE_CLS[it.tcg_franchise] || FRANCHISE_CLS.Lainnya}>{it.tcg_franchise}</Badge>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button onClick={() => { setEditing(it); setModal(true); }} data-testid={`edit-inv-${it.id}`} className="p-1.5 rounded text-slate-400 hover:text-indigo-300 hover:bg-slate-800">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => del(it.id)} data-testid={`delete-inv-${it.id}`} className="p-1.5 rounded text-slate-400 hover:text-rose-300 hover:bg-slate-800">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <h3 className="font-heading font-semibold text-white leading-snug">{it.nama_produk}</h3>
                <p className="text-xs text-slate-500 mt-0.5 min-h-[16px]">
                  {[it.set_name, it.type_sealed, it.rarity, it.condition, it.grading_company && `${it.grading_company} ${it.grade_score}`]
                    .filter(Boolean)
                    .join(" • ")}
                </p>
                <div className="flex items-end justify-between mt-4 pt-3 border-t border-slate-800">
                  <div>
                    <p className="text-[10px] font-mono uppercase text-slate-500">Harga Jual</p>
                    <p className="font-mono font-bold text-emerald-300">{formatRupiah(it.harga_jual)}</p>
                    {isAdmin && <p className="text-[10px] text-slate-500 font-mono">Modal {formatRupiah(it.harga_beli)}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-mono uppercase text-slate-500">Stok</p>
                    <p className={`font-mono font-black text-lg ${it.stok_qty <= 0 ? "text-rose-400" : "text-white"}`}>{it.stok_qty}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ItemModal open={modal} onClose={() => setModal(false)} onSaved={load} editing={editing} allowed={allowed} />
      <BulkModal open={bulkOpen} onClose={() => setBulkOpen(false)} onSaved={load} />
    </div>
  );
}
